import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ticketsApi } from '@/api/tickets.api';
import { TicketTable } from '@/components/tickets/TicketTable';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { useToasts } from '@/context/ToastContext';

/**
 * V4.9.2+ — Deleted tickets list.
 *
 * The previous version dumped a `Restore BDS-XXXXX` button for every
 * ticket in a chaotic grid below the table. Hard to scan, ugly, and you
 * had to mentally cross-reference the button text with the ticket row.
 *
 * This version puts a single `Restore` button INLINE on each ticket row
 * via the new `rowAction` prop on TicketTable. One click restores the
 * ticket and removes it from this list (cache invalidation reloads).
 */
export function DeletedPage() {
  const queryClient = useQueryClient();
  const { push } = useToasts();
  const query = useQuery({ queryKey: ['deletedTickets'], queryFn: () => ticketsApi.listDeleted() });
  const restore = useMutation({
    mutationFn: (ticketId: string) => ticketsApi.restoreDeleted(ticketId),
    onSuccess: (_, ticketId) => {
      push({ kind: 'success', title: 'Ticket restored', message: `${ticketId} is back in the active list.` });
      void queryClient.invalidateQueries({ queryKey: ['deletedTickets'] });
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
    },
    onError: (err: unknown) => {
      push({
        kind: 'error',
        title: 'Restore failed',
        message: err instanceof Error ? err.message : 'Try refreshing and retrying.'
      });
    }
  });

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">
          Deleted tickets
          {query.data ? <span className="text-muted text-small"> · {query.data.length}</span> : null}
        </h3>
      </div>
      <div className="card-body no-pad">
        {query.isLoading ? <LoadingState /> : null}
        {query.isError ? <ErrorState error={query.error} onRetry={() => query.refetch()} /> : null}
        {query.data ? (
          <TicketTable
            tickets={query.data}
            emptyLabel="Nothing in the deleted bin."
            rowActionLabel="Restore"
            rowAction={(ticket) => (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={restore.isPending && restore.variables === ticket.id}
                onClick={() => {
                  if (window.confirm(`Restore ${ticket.id}? It will reappear in the active tickets list.`)) {
                    restore.mutate(ticket.id);
                  }
                }}
              >
                {restore.isPending && restore.variables === ticket.id ? 'Restoring…' : 'Restore'}
              </button>
            )}
          />
        ) : null}
      </div>
    </div>
  );
}
